import { areEqual } from 'utils/ArrayUtils';
import { DiagramDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData';
import { syncDiagramsIds } from './DataDesignerUtils';

export type DataDesignerView = 'Diagram View' | 'List View';

export interface DataDesignerReducerState {
  diagrams: DiagramDisplayData[];
  selectedDiagramIds: string[];
  expandedDiagramIds: string[];

  currentView: DataDesignerView;
  addedDiagramIdsInListView: string[];
  layerForDiagramId: string | null;
}

export type DataDesignerReducerAction =
  | { type: 'set-diagrams'; diagrams: DiagramDisplayData[] }
  | { type: 'update-diagram'; diagram: DiagramDisplayData }
  | { type: 'delete-diagrams'; diagramIds: string[] }
  | { type: 'select-diagrams'; diagramIds: string[] }
  | { type: 'add-diagram'; input: DiagramDisplayData }
  | { type: 'change-view'; view: DataDesignerView }
  | { type: 'hide-name-layer' }
  //TODO alex: Better to create DataStructure on the top of "DiagramDisplayData" and have "isExpanded" and "isSelected" flags there
  | { type: 'expand-diagram'; diagramId: string }
  | { type: 'collapse-diagram'; diagramId: string }
  | { type: 'unselect-diagrams' };

export const dataDesignerReducer: React.Reducer<DataDesignerReducerState, DataDesignerReducerAction> = (
  state,
  action
) => {
  switch (action.type) {
    case 'set-diagrams':
      return {
        ...state,
        diagrams: action.diagrams,
        selectedDiagramIds: syncDiagramsIds(action.diagrams, state.selectedDiagramIds),
        expandedDiagramIds: action.diagrams.filter(item => !item.IsSimpleType).map(item => item.Id),
      };
    case 'update-diagram':
      {
        const inputIndex = state.diagrams?.findIndex(input => input.Id === action.diagram.Id) ?? -1;

        if (inputIndex !== -1) {
          const isSimpleTypeChanged = state.diagrams[inputIndex].IsSimpleType !== action.diagram.IsSimpleType;
          state.diagrams[inputIndex] = { ...action.diagram };

          let expandedDiagramIds = state.expandedDiagramIds;
          if (isSimpleTypeChanged) {
            if (state.diagrams[inputIndex].IsSimpleType) {
              expandedDiagramIds = state.expandedDiagramIds.filter(id => id !== action.diagram.Id);
            } else {
              expandedDiagramIds = [...state.expandedDiagramIds, action.diagram.Id];
            }
          }

          return {
            ...state,
            diagrams: [...state.diagrams],
            inputNameLayerFor: null,
            expandedDiagramIds,
          };
        } else {
          console.warn(`Input "${action.diagram.DataDescription.Name}" is not found`);
        }
      }
      break;
    case 'delete-diagrams': {
      const diagrams = state.diagrams.filter(input => !action.diagramIds.some(deleteInput => deleteInput === input.Id));

      let selectedInputsIds: string[] = [];

      if (action.diagramIds.length === 1 && state.currentView === 'List View') {
        const deleteInputId = action.diagramIds[0];
        const nextInputIndex = state.diagrams.findIndex(item => item.Id === deleteInputId);
        const nextInput = state.diagrams[nextInputIndex + 1];
        if (nextInput) {
          //select next row in list view when delete row
          selectedInputsIds = [nextInput.Id];
        }
      } else {
        selectedInputsIds = syncDiagramsIds(diagrams, state.selectedDiagramIds);
      }

      return {
        ...state,
        diagrams: diagrams,
        selectedDiagramIds: selectedInputsIds,
        expandedDiagramIds: syncDiagramsIds(diagrams, state.expandedDiagramIds),
      };
    }
    case 'select-diagrams': {
      // Optimization to prevent DesignerSurface re-rendering
      if (areEqual(action.diagramIds, state.selectedDiagramIds)) {
        return state;
      }

      return {
        ...state,
        selectedDiagramIds: action.diagramIds,
      };
    }
    case 'add-diagram': {
      const inputs = state.diagrams.concat(action.input);

      const selectedDiagramIds =
        state.currentView === 'Diagram View' ||
        (state.currentView === 'List View' && state.selectedDiagramIds.length === 0)
          ? syncDiagramsIds(inputs, [action.input.Id])
          : state.selectedDiagramIds;

      const expandedDiagramIds = [...state.expandedDiagramIds];
      if (!action.input.IsSimpleType) {
        expandedDiagramIds.push(action.input.Id);
      }

      return {
        ...state,
        diagrams: inputs,

        //TODO alex: need to add a unit test to check that added input is auto selected
        // auto select added input only in Diagram View
        selectedDiagramIds,
        addedDiagramIdsInListView:
          state.currentView === 'List View' ? state.addedDiagramIdsInListView.concat([action.input.Id]) : [],
        layerForDiagramId: state.currentView === 'Diagram View' ? action.input.Id : null,
        expandedDiagramIds,
      };
    }
    case 'change-view': {
      return {
        ...state,
        currentView: action.view,
        addedDiagramIdsInListView: [],
      };
    }
    case 'hide-name-layer': {
      return {
        ...state,
        layerForDiagramId: null,
      };
    }
    case 'expand-diagram': {
      return {
        ...state,
        expandedDiagramIds: [...state.expandedDiagramIds, action.diagramId],
      };
    }
    case 'collapse-diagram': {
      return {
        ...state,
        expandedDiagramIds: state.expandedDiagramIds.filter(id => id !== action.diagramId),
      };
    }
    case 'unselect-diagrams': {
      return {
        ...state,
        selectedDiagramIds: [],
      };
    }
  }

  return state;
};

export function getDefaultState(currentView: DataDesignerView): DataDesignerReducerState {
  return {
    diagrams: [],
    selectedDiagramIds: [],
    expandedDiagramIds: [],

    currentView,
    addedDiagramIdsInListView: [],
    layerForDiagramId: null,
  };
}
